import { MODEL_TO_AVAILABLE_THINKING_LEVELS, MODEL_TO_DEFAULT_THINKING_LEVEL } from "~/constants/model";
import { isDefined } from "~/shared/common";
import { AiModel, IconKind, PageKind, SkillKind } from "~/shared/enums";
import type { Skill, SkillUpdate, TransactionResponse } from "~/shared/types";
import { makeRandomColorHex } from "~/utils/color";
import { filterInPlace, makeDuid, makeEmptyLexicalState } from "~/utils/common";
import { skillComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSorted, setObjectMapAndSorted, updateInSorted } from "./utils";

export type State = {
  _duidsToSkills: Map<string, Skill>;
  _sortedSkillDuids: string[];
};

export const state: State = {
  _duidsToSkills: new Map(),
  _sortedSkillDuids: [],
};

export type Getters = {
  skillList: (options?: { includeHidden?: boolean }) => Skill[];
  favoriteSkillList: () => Skill[];
  getSkillByDuid: (duid: string) => Skill | undefined;
};

export type Actions = {
  createSkill: (
    title: string,
    order: string,
    partialSkill?: Partial<Skill>,
    options?: { awaitBackend?: boolean }
  ) => Promise<Skill | undefined>;
  updateSkill: (
    update: SkillUpdate,
    options?: { awaitBackend?: boolean; noBackend?: boolean }
  ) => Promise<Skill | undefined>;
  updateSkillAccessors: (
    skillDuid: string,
    accessorDuids: string[],
    add?: boolean,
    options?: { awaitBackend?: boolean }
  ) => Promise<Skill | undefined>;
  deleteSkill: (skill: Skill, options?: { awaitBackend?: boolean }) => Promise<void>;
  $setSkills: (skills: Skill[]) => void;
  $createOrUpdateSkill: (skill: Skill) => void;
  $deleteSkill: (skill: Skill) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  skillList() {
    return (options = {}) => {
      const { includeHidden = false } = options;
      const skills = this._sortedSkillDuids.map((e) => this._duidsToSkills.get(e)).filter(isDefined);
      return includeHidden ? skills : skills.filter((e) => !e.hidden);
    };
  },
  favoriteSkillList() {
    return () => {
      const skills = this._sortedSkillDuids.map((e) => this._duidsToSkills.get(e)).filter(isDefined);
      return skills.filter((e) => !e.hidden && e.favorite);
    };
  },
  getSkillByDuid() {
    return (duid) => this._duidsToSkills.get(duid);
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async createSkill(title, order, partialSkill = {}, options = {}) {
    const { awaitBackend = false } = options;
    const userStore = this.$useUserStore();
    const aiModel = userStore.aiModel ?? AiModel.AUTO;
    const userThinkingLevel = userStore.aiThinkingLevel;
    const aiThinkingLevel = (MODEL_TO_AVAILABLE_THINKING_LEVELS.get(aiModel) ?? []).includes(userThinkingLevel)
      ? userThinkingLevel
      : MODEL_TO_DEFAULT_THINKING_LEVEL[aiModel];
    const skill: Skill = {
      pageKind: PageKind.SKILL,
      duid: makeDuid(),
      kind: SkillKind.CUSTOM,
      title,
      order,
      prompt: makeEmptyLexicalState(),
      iconKind: IconKind.NONE,
      iconNameOrEmoji: "",
      colorHex: makeRandomColorHex(),
      hidden: false,
      favorite: false,
      accessibleByTeam: true,
      accessibleByUserDuids: [],
      attachmentDuids: [],
      extras: [],
      aiModel,
      aiThinkingLevel,
      aiWebEnabled: true,
      recurrence: null,
      recursNextAt: null,
      ...partialSkill,
    };

    this.$createOrUpdateSkill(skill);

    const backendAction = this.$backend.skill.create(skill);
    if (awaitBackend) {
      await backendAction;
    }
    return skill;
  },
  async updateSkill(update, options = {}) {
    const { awaitBackend = false, noBackend = false } = options;
    const skill = this.getSkillByDuid(update.duid);
    if (!skill) {
      return undefined;
    }

    Object.assign(skill, update);
    updateInSorted(this._duidsToSkills, this._sortedSkillDuids, skill.duid, skillComparator);
    if (noBackend) {
      return skill;
    }

    const backendAction = this.$backend.skill.update(update);
    if (awaitBackend) {
      await backendAction;
    }
    return skill;
  },

  async updateSkillAccessors(duid, accessorDuids, add, options = {}) {
    const { awaitBackend = false } = options;
    const skill = this.getSkillByDuid(duid);
    if (!skill) {
      return undefined;
    }

    let backendAction: Promise<TransactionResponse>;

    const payload = {
      duid: skill.duid,
      accessibleByUserDuids: accessorDuids,
    };

    if (add) {
      skill.accessibleByUserDuids.push(...accessorDuids);
      backendAction = this.$backend.skill.updateListAdd(payload);
    } else {
      filterInPlace(skill.accessibleByUserDuids, (e) => !accessorDuids.includes(e));
      backendAction = this.$backend.skill.updateListRemove(payload);
    }

    if (awaitBackend) {
      await backendAction;
    }

    return skill;
  },

  async deleteSkill(skill, options = {}) {
    const { awaitBackend = false } = options;

    this.$deleteSkill(skill);

    const backendAction = this.$backend.skill.delete(skill.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $setSkills(skills) {
    setObjectMapAndSorted(
      this._duidsToSkills,
      this._sortedSkillDuids,
      skills.map((e) => ({ ...e, pageKind: PageKind.SKILL }) satisfies Skill),
      skillComparator
    );
  },
  $createOrUpdateSkill(skill) {
    const currentSkill = this.getSkillByDuid(skill.duid);

    const userStore = this.$useUserStore();
    if (!(skill.accessibleByTeam || skill.accessibleByUserDuids.includes(userStore.duid))) {
      if (currentSkill) {
        this.$deleteSkill(skill);
      }
      return;
    }

    // eslint-disable-next-line no-param-reassign
    skill.pageKind = PageKind.SKILL;

    if (!currentSkill) {
      this._duidsToSkills.set(skill.duid, skill);
    } else {
      Object.assign(currentSkill, skill);
    }
    updateInSorted(this._duidsToSkills, this._sortedSkillDuids, skill.duid, skillComparator);
  },
  $deleteSkill(skill) {
    this._duidsToSkills.delete(skill.duid);
    removeFromSorted(this._sortedSkillDuids, skill.duid);
  },
};

export { actions, getters };
